import type { ReactNode } from 'react';
import { CheckCircle2, Info, ShieldAlert, TriangleAlert } from 'lucide-react';

/**
 * The four things the product ever has to tell somebody, and how each one looks.
 *
 * One definition, read by both places a message is drawn — the inline `Alert`
 * and the `Toaster` — so a save that succeeds carries the same mark whether it
 * is announced in the corner or on the page itself (CLAUDE.md §3). Two copies of
 * this table is how "saved" ends up one green in a toast and another green in a
 * banner.
 *
 * **Bad news keeps the product's status tokens; good news wears the console's
 * blue.** The three that report a problem — critical, warning, info — must look
 * the same in every room, or they stop being read as statuses at all. Success
 * is the exception, and deliberately: "saved" is not a warning about anything,
 * it is the console agreeing with what somebody just did, and a green panel
 * flashing over a blue console reads as a different product interrupting.
 *
 * The blue is written as literal utilities rather than `bg-accent`, because a
 * toast is rendered into `document.body` — outside whatever page raised it — so
 * a page-scoped `--accent` never reaches it, and the global one is still the
 * product's teal.
 *
 * This is the *message* table only. A record's own standing — "Enrolled",
 * "Paid", "Present" — is `Badge`'s table, and stays green, because a list of
 * records is read by scanning green against amber against red.
 */
export type StatusTone = 'success' | 'info' | 'warning' | 'critical';

export interface StatusToneStyle {
  icon: ReactNode;
  /** The soft tile the icon sits in. */
  tile: string;
  /** The rule down the leading edge, which is what carries the tone at a glance. */
  bar: string;
  /** The tinted ground of a `note` — a standing fact on the page, not a message. */
  note: string;
}

export const STATUS_TONE: Record<StatusTone, StatusToneStyle> = {
  success: {
    icon: <CheckCircle2 className="h-4 w-4" />,
    tile: 'bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400',
    bar: 'bg-gradient-to-b from-blue-500 to-indigo-600',
    note: 'bg-blue-50 dark:bg-blue-950/60',
  },
  info: {
    icon: <Info className="h-4 w-4" />,
    tile: 'bg-info-bg text-info',
    bar: 'bg-info',
    note: 'bg-info-bg',
  },
  warning: {
    icon: <TriangleAlert className="h-4 w-4" />,
    tile: 'bg-warn-bg text-warn',
    bar: 'bg-warn',
    note: 'bg-warn-bg',
  },
  critical: {
    icon: <ShieldAlert className="h-4 w-4" />,
    tile: 'bg-crit-bg text-crit',
    bar: 'bg-crit',
    note: 'bg-crit-bg',
  },
};

/**
 * The shell both message surfaces are built on.
 *
 * A panel in the console's own language — the same radius, border, surface and
 * shadow as every card on the redesigned screens — with the tone carried by the
 * edge rule and the icon tile rather than by flooding the whole block with
 * colour. That is what lets a message sit inside a form without looking like a
 * different product, and lets a toast sit over one.
 */
/**
 * The shell of a `note`: the same shape as a message, flat on its tint.
 *
 * No shadow and no entrance — a lifted white panel that fades in is what a
 * toast looks like, and a standing fact about the page ("you can only look at
 * this register") drawn that way was read as a notification that had landed.
 */
export const NOTE_SHELL =
  'relative flex items-start gap-3 overflow-hidden rounded-2xl border border-line pl-4 pr-3.5 py-3.5';

export const STATUS_SHELL =
  'relative flex items-start gap-3 overflow-hidden rounded-2xl border border-line bg-surface pl-4 pr-3.5 py-3.5 shadow-[0_10px_28px_-14px_rgba(15,23,42,0.35)]';
